import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import Sidebar from './components/common/Sidebar';
import Header from './components/common/Header';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CreateCertificate from './pages/CreateCertificate';
import CertificateHistory from './pages/CertificateHistory';
import StaffManagement from './pages/StaffManagement';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import VerifyCertificate from './pages/VerifyCertificate';
import PrintCertificate from './pages/PrintCertificate';
import TemplateManagement from './pages/TemplateManagement';
import NotFound from './pages/NotFound';
import './index.css';

const ProtectedRoute = ({ children, adminOnly = false }) => {
    const { user, loading, isAuthenticated } = useAuth();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <LoadingSpinner size="lg" text="Loading..." />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (adminOnly && user?.role !== 'admin') {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();

    // Close sidebar on route change (mobile)
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    const closeSidebar = () => setSidebarOpen(false);

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
                    onClick={closeSidebar}
                />
            )}

            <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
            <div
                className={`transition-all duration-300 lg:ml-64 ml-0`}
            >
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} />
                <main className="p-3 sm:p-6">{children}</main>
            </div>
        </div>
    );
};

function App() {
    return (
        <ErrorBoundary>
            <LanguageProvider>
                <AuthProvider>
                    <Router>
                        <Toaster position="top-right" />
                        <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route
                                path="/dashboard"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <Dashboard />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/create-certificate"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <CreateCertificate />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/certificate-history"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <CertificateHistory />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/staff"
                                element={
                                    <ProtectedRoute adminOnly>
                                        <Layout>
                                            <StaffManagement />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/settings"
                                element={
                                    <ProtectedRoute adminOnly>
                                        <Layout>
                                            <Settings />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/reports"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <Reports />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/template-management"
                                element={
                                    <ProtectedRoute adminOnly>
                                        <Layout>
                                            <TemplateManagement />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/verify/:certificateNo?"
                                element={<VerifyCertificate />}
                            />
                            <Route
                                path="/print-certificate/:id"
                                element={
                                    <ProtectedRoute>
                                        <PrintCertificate />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Router>
                </AuthProvider>
            </LanguageProvider>
        </ErrorBoundary>
    );
}

export default App;